import { Divider, Stack, Typography } from "@mui/material";
import type { BudgetOverview, BudgetOverviewExpensesEntryMode, BudgetOverviewIncomesEntryMode } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatCurrencyWithoutCode } from "Infrastructure/Utils/CurrencyUtils";
import type { FC } from "react";
import { RecapChart } from "./RecapChart";
import { RecapRow } from "./RecapRow";

type Props = {
  readonly overview?: BudgetOverview;
  // Individual-mode per-category totals, pre-computed by BudgetPane (same source it uses for its
  // own recalculation logic and expense-tile grid, kept here to avoid re-deriving them twice).
  readonly incomesTotal: number;
  readonly housingTotal: number;
  readonly consumptionTotal: number;
  readonly lifeStyleTotal: number;
  readonly otherCategoryTotal: number;
  readonly insuranceItemsTotal: number;
  readonly financialProductItemsTotal: number;
  readonly loanItemsTotal: number;
  readonly allocatedGoalAmount?: number;
  readonly isTablet?: boolean;
  readonly incomesEntryMode?: BudgetOverviewIncomesEntryMode;
  readonly expensesEntryMode?: BudgetOverviewExpensesEntryMode;
  readonly liveIncomesAmount?: number;
  readonly liveInsuranceExpenses?: number;
  readonly liveFinancialProductExpenses?: number;
  readonly liveLoanExpenses?: number;
  readonly liveOtherExpenses?: number;
};

export const Recap: FC<Props> = ({
  overview,
  incomesTotal,
  housingTotal,
  consumptionTotal,
  lifeStyleTotal,
  otherCategoryTotal,
  insuranceItemsTotal,
  financialProductItemsTotal,
  loanItemsTotal,
  allocatedGoalAmount,
  isTablet,
  incomesEntryMode,
  expensesEntryMode,
  liveIncomesAmount,
  liveInsuranceExpenses,
  liveFinancialProductExpenses,
  liveLoanExpenses,
  liveOtherExpenses,
}) => {
  const { t } = useResource();

  const getIncomesAmount = () => {
    const mode = incomesEntryMode ?? overview?.incomesEntryMode;
    if (mode === "aggregate") {
      return liveIncomesAmount ?? overview?.aggregatedIncomes?.aggregatedAmount?.value ?? 0;
    }
    if (mode === "individual") {
      return incomesTotal;
    }

    return 0;
  };

  const getIncomesAmountFormatted = () => {
    const amount = getIncomesAmount();

    return formatCurrencyWithoutCode(amount, 2);
  };

  const getInsuranceExpensesAmount = () => {
    const mode = expensesEntryMode ?? overview?.expensesEntryMode;
    if (mode === "aggregate") {
      return liveInsuranceExpenses ?? overview?.aggregatedInsuranceExpenses?.aggregatedAmount?.value ?? 0;
    }
    if (mode === "individual") {
      return insuranceItemsTotal;
    }

    return 0;
  };

  const getFinancialProductExpensesAmount = () => {
    const mode = expensesEntryMode ?? overview?.expensesEntryMode;
    if (mode === "aggregate") {
      return liveFinancialProductExpenses ?? overview?.aggregatedFinancialProductExpenses?.aggregatedAmount?.value ?? 0;
    }
    if (mode === "individual") {
      return financialProductItemsTotal;
    }

    return 0;
  };

  const getLoanExpensesAmount = () => {
    const mode = expensesEntryMode ?? overview?.expensesEntryMode;
    if (mode === "aggregate") {
      return liveLoanExpenses ?? overview?.aggregatedLoanExpenses?.aggregatedAmount?.value ?? 0;
    }
    if (mode === "individual") {
      return loanItemsTotal;
    }

    return 0;
  };

  // Non-obvious rollup: in Individual mode this combines Other+Consumption+LifeStyle+Housing (i.e.
  // everything that isn't Insurance/FinancialProduct/Loan), matching the old
  // budget.aggregatedExpenses.otherExpenses bucket. In Aggregate mode it reads
  // overview.aggregatedOtherExpenses directly, by analogy with the old combined-bucket field of the
  // same name (see final report for the caveat on this interpretation).
  const getOtherExpensesAmount = () => {
    const mode = expensesEntryMode ?? overview?.expensesEntryMode;
    if (mode === "aggregate") {
      return liveOtherExpenses ?? overview?.aggregatedOtherExpenses?.aggregatedAmount?.value ?? 0;
    }
    if (mode === "individual") {
      return housingTotal + consumptionTotal + lifeStyleTotal + otherCategoryTotal;
    }

    return 0;
  };

  const getExpensesAmount = () => {
    return (
      (getInsuranceExpensesAmount() ?? 0) +
      (getFinancialProductExpensesAmount() ?? 0) +
      (getLoanExpensesAmount() ?? 0) +
      (getOtherExpensesAmount() ?? 0)
    );
  };

  const getExpensesAmountFormatted = () => {
    const amount = getExpensesAmount();

    return formatCurrencyWithoutCode(amount, 2);
  };

  const getDifference = () => {
    const incomes = getIncomesAmount();
    const expenses = getExpensesAmount();

    return incomes - expenses;
  };

  const getDifferenceFormatted = () => {
    const difference = getDifference();

    return formatCurrencyWithoutCode(difference, 2);
  };

  const getAllocatedGoalAmount = (): number =>
    (allocatedGoalAmount ?? overview?.allocatedGoalAmount?.allocatedGoalAmount?.value ?? 0) as number;

  const getReserve = () => {
    const allocated = getAllocatedGoalAmount();
    const difference = getDifference();
    const remaining = difference - allocated;

    if (remaining > 0) {
      return formatCurrencyWithoutCode(remaining, 2);
    }

    return formatCurrencyWithoutCode(0, 2);
  };

  const formatExpense = (amount: number): string => {
    const formatted = formatCurrencyWithoutCode(amount, 2);
    return amount > 0 ? `- ${formatted}` : formatted;
  };

  return (
    <Stack spacing={Spacing.XL} pt={isTablet ? Spacing.XL : 0} pb={Spacing.XXXL} px={isTablet ? 0 : Spacing.XXXL} sx={{ width: 400 }}>
      <Typography variant="subtitle1" fontWeight={600}>
        {t(Resources.AnalysisDetail.Budget.Recap.Title)}
      </Typography>

      <RecapChart
        incomesAmount={getIncomesAmount()}
        insuranceExpenses={getInsuranceExpensesAmount()}
        financialProductExpenses={getFinancialProductExpensesAmount()}
        loanExpenses={getLoanExpensesAmount()}
        otherExpenses={getOtherExpensesAmount()}
      />

      <Typography variant="body2" fontWeight={600}>
        {t(Resources.AnalysisDetail.Budget.Recap.DetailedRecap)}
      </Typography>

      <RecapRow title={t(Resources.AnalysisDetail.Budget.Recap.Incomes) as string} amount={`+ ${getIncomesAmountFormatted()}`} />
      <RecapRow title={t(Resources.AnalysisDetail.Budget.Recap.Expenses) as string} amount={`- ${getExpensesAmountFormatted()}`} />

      <Divider />

      <RecapRow
        title={t(Resources.AnalysisDetail.Budget.Recap.ExpensesInsurance) as string}
        amount={`${formatExpense(getInsuranceExpensesAmount())}`}
      />
      <RecapRow
        title={t(Resources.AnalysisDetail.Budget.Recap.ExpensesFinancialProduct) as string}
        amount={`${formatExpense(getFinancialProductExpensesAmount())}`}
        isHighlighted
        isFinancialProduct
      />
      <RecapRow
        title={t(Resources.AnalysisDetail.Budget.Recap.ExpensesLoan) as string}
        amount={`${formatExpense(getLoanExpensesAmount())}`}
      />
      <RecapRow
        title={t(Resources.AnalysisDetail.Budget.Recap.ExpensesOther) as string}
        amount={`${formatExpense(getOtherExpensesAmount())}`}
      />

      <Divider />

      <RecapRow
        title={t(Resources.AnalysisDetail.Budget.Recap.Difference) as string}
        amount={getDifferenceFormatted()}
        isTitleSemiBold
        isHighlighted
        tooltip={t(Resources.AnalysisDetail.Budget.Recap.DifferenceTooltip) as string}
      />
      <RecapRow title={t(Resources.AnalysisDetail.Budget.Recap.NotInvestedReserve) as string} amount={getReserve()} />

      <Divider />

      <RecapRow
        title={t(Resources.AnalysisDetail.Budget.Recap.AllocatedGoalAmount) as string}
        amount={`${formatCurrencyWithoutCode(getAllocatedGoalAmount() + getFinancialProductExpensesAmount(), 2)}`}
        isHighlighted
        tooltip={t(Resources.AnalysisDetail.Budget.Recap.AllocatedGoalAmountTooltip) as string}
      />
    </Stack>
  );
};
